import React from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../ui/tooltip";
import { cn } from "../../../lib/utils";
import { pct as fmtPct } from "../utils/format";

/**
 * The bar vocabulary for the Player Insights tab.
 *
 * No pie charts with more than four slices (§8) — every mix on this page is a
 * 100% horizontal bar and every distribution is a bar list or a column set.
 * Flat fills, rounded caps, no gradients on data ink, no drop shadows.
 *
 * Colours are ASTRA theme tokens: primary is the casino / first series,
 * chart-sports the second, and the fourth ramp step is deliberately colourless
 * because it is always the "Other" remainder.
 */

export const RAMP_CLASSES = [
  "bg-primary",
  "bg-chart-sports",
  "bg-chart-sports/40",
  "bg-muted-foreground/30",
];

export const REMAINDER_CLASS = "bg-muted-foreground/30";

/** Colour class for slice `index`; the "Other" remainder always takes the grey. */
export const rampClass = (index, isRemainder) =>
  isRemainder ? REMAINDER_CLASS : RAMP_CLASSES[Math.min(index, RAMP_CLASSES.length - 2)];

/** Wraps any element in the page's standard small tooltip card. */
export function HoverTip({ label, children, side = "top" }) {
  return (
    <Tooltip delayDuration={80}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} className="border bg-popover px-2.5 py-1.5 text-sm text-popover-foreground shadow-md">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * 100% stacked horizontal bar. `slices` are `{ label, pct, isRemainder, colorClass }`
 * rendered at their given percentages — never rescaled to fill the track (§2.2).
 */
export function ShareBar({ slices, height = 10, showLegend = true, legendValue, className }) {
  if (!slices || !slices.length) return null;

  return (
    <div className={className}>
      <div className="flex w-full overflow-hidden rounded-full bg-muted" style={{ height }}>
        {slices.map((slice, index) => (
          <HoverTip
            key={slice.label}
            label={
              <span className="tabular-nums">
                {slice.label} · {fmtPct(slice.pct)}
                {slice.tipDetail ? ` · ${slice.tipDetail}` : ""}
              </span>
            }
          >
            <div
              className={cn(
                "h-full cursor-default transition-all duration-200 hover:opacity-80",
                slice.colorClass || rampClass(index, slice.isRemainder)
              )}
              style={{ width: `${slice.pct}%` }}
            />
          </HoverTip>
        ))}
      </div>

      {showLegend && (
        <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5">
          {slices.map((slice, index) => (
            <span key={slice.label} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <span
                className={cn("h-2 w-2 shrink-0 rounded-sm", slice.colorClass || rampClass(index, slice.isRemainder))}
                aria-hidden="true"
              />
              <span>{slice.label}</span>
              <span className="font-medium tabular-nums text-foreground">
                {legendValue ? legendValue(slice) : fmtPct(slice.pct)}
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Labelled horizontal bars — stake mixes, cashout buckets, deposits, market
 * weights. Bars scale against `max` (default: the largest row), so a 30% bar
 * reads as 30% of the widest value rather than 30% of the card.
 */
export function BarList({
  rows,
  max,
  height = 8,
  colorClass = "bg-primary",
  labelWidth = 64,
  valueWidth = 52,
  className,
  emptySlotHint,
}) {
  if (!rows || !rows.length) return null;
  const ceiling = max || Math.max(...rows.map((row) => row.value), 1);

  return (
    <ul className={cn("space-y-2", className)}>
      {rows.map((row) => {
        const isEmpty = !row.value;
        return (
          <li key={row.label} className="flex items-center gap-3">
            <span
              className="shrink-0 truncate text-xs tabular-nums text-muted-foreground"
              style={{ width: labelWidth }}
              title={row.label}
            >
              {row.label}
            </span>
            <div className="min-w-0 flex-1">
              <HoverTip label={<span className="tabular-nums">{row.tip || `${row.label} · ${row.display}`}</span>}>
                <div className="flex w-full items-center rounded-full bg-muted" style={{ height }}>
                  <div
                    className={cn(
                      "h-full rounded-full transition-all duration-200",
                      row.colorClass || (row.muted ? REMAINDER_CLASS : colorClass)
                    )}
                    style={{ width: `${ceiling ? Math.max((row.value / ceiling) * 100, isEmpty ? 0 : 1.5) : 0}%` }}
                  />
                </div>
              </HoverTip>
            </div>
            <span
              className={cn(
                "shrink-0 text-right text-xs font-medium tabular-nums",
                isEmpty ? "text-muted-foreground" : "text-foreground"
              )}
              style={{ width: valueWidth }}
            >
              {isEmpty && emptySlotHint ? emptySlotHint : row.display}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Column distribution — used for odds appetite, where the barbell shape (heavy
 * at both ends) is the whole insight and only a vertical arrangement shows it.
 * Zero-value buckets keep their slot as a faint stub rather than disappearing.
 */
export function ColumnChart({ rows, height = 132, colorClass = "bg-chart-sports", className }) {
  if (!rows || !rows.length) return null;
  const ceiling = Math.max(...rows.map((row) => row.value), 1);

  return (
    <div className={className}>
      <div className="flex items-end gap-1.5" style={{ height }}>
        {rows.map((row) => (
          <div key={row.label} className="flex h-full flex-1 flex-col justify-end gap-1">
            <span className="text-center text-xs font-medium tabular-nums text-foreground">
              {row.value ? row.display : ""}
            </span>
            <HoverTip label={<span className="tabular-nums">{row.tip || `${row.label} · ${row.display}`}</span>}>
              <div
                className={cn(
                  "w-full rounded-t-sm transition-all duration-200",
                  row.value ? row.colorClass || colorClass : "bg-muted-foreground/20"
                )}
                style={{
                  height: `${Math.max((row.value / ceiling) * 100, row.value ? 2 : 1)}%`,
                  minHeight: row.value ? 3 : 2,
                }}
              />
            </HoverTip>
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex gap-1.5">
        {rows.map((row) => (
          <span key={row.label} className="flex-1 text-center text-xs leading-tight text-muted-foreground">
            {row.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/** In-cell bar for a table column, paired with its own number. */
export function CellBar({ value, max, display, colorClass = "bg-primary", emphasis }) {
  const width = max ? Math.max((value / max) * 100, value ? 2 : 0) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-full min-w-[36px] max-w-[92px] overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full transition-all duration-200", colorClass, !emphasis && "opacity-75")}
          style={{ width: `${width}%` }}
        />
      </div>
      <span className={cn("shrink-0 text-right text-sm tabular-nums text-foreground", emphasis && "font-semibold")}>
        {display}
      </span>
    </div>
  );
}

/** Compact stacked bar for a single row of parts — Plinko risk levels. */
export function MiniStack({ parts, height = 8 }) {
  const total = parts.reduce((sum, part) => sum + (part.pct || 0), 0) || 100;
  return (
    <div className="flex w-full overflow-hidden rounded-full bg-muted" style={{ height }}>
      {parts.map((part, index) =>
        part.pct ? (
          <HoverTip key={part.label} label={<span className="tabular-nums">{`${part.label} · ${fmtPct(part.pct)}`}</span>}>
            <div
              className={cn("h-full cursor-default transition-all duration-200", RAMP_CLASSES[Math.min(index, 2)])}
              style={{ width: `${(part.pct / total) * 100}%` }}
            />
          </HoverTip>
        ) : null
      )}
    </div>
  );
}
